<script setup>
import { ref, computed } from 'vue'
import { useInvoiceStore } from '../stores/invoiceStore'
import StatCard from '../components/StatCard.vue'
import InvoicePreview from '../components/InvoicePreview.vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import ServicesManageModal from '../components/ServicesManageModal.vue'
import CustomDropdown from '../components/CustomDropdown.vue'

const invoiceStore = useInvoiceStore()
const searchQuery = ref('')
const filters = ref({
  status: 'all',
  client: 'all',
  dateFrom: '',
  dateTo: ''
})

const invoices = computed(() => invoiceStore.invoices)
const stats = computed(() => invoiceStore.stats)

// Filtered invoices
const filteredInvoices = computed(() => {
  let result = [...invoices.value]
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(invoice => 
      invoice.userName.toLowerCase().includes(query) ||
      invoice.fixtureName.toLowerCase().includes(query) ||
      invoice.id.toLowerCase().includes(query)
    )
  }

  if (filters.value.client !== 'all') {
    result = result.filter(invoice => invoice.userName === filters.value.client)
  }

  if (filters.value.status !== 'all') {
    result = result.filter(invoice => invoice.status === filters.value.status)
  }

  return result
})

// Add these new refs
const selectedInvoice = ref(null)
const isPreviewOpen = ref(false)
const isCreateModalOpen = ref(false)
const newInvoice = ref({
  userName: '',
  fixtureName: '',
  league: '',
  amount: '',
  paymentMethod: 'credit_card',
  cardLast4: '',
  status: 'paid',
  purchaseDate: '',
  dueDate: ''
})

// Add to your existing refs
const invoiceItems = ref([])

// Add computed property for subtotal
const subtotal = computed(() => {
  return invoiceItems.value.reduce((sum, item) => {
    return sum + (item.quantity * item.price)
  }, 0)
})

// Add method to add new invoice items
const addInvoiceItem = () => {
  invoiceItems.value.push({
    description: '',
    details: '',
    quantity: 1,
    price: 0
  })
}

// Add this new method
const openPreview = (invoice) => {
  selectedInvoice.value = invoice
  isPreviewOpen.value = true
}

// Add this helper function before downloadInvoice
const formatPDFDate = (dateString) => {
  try {
    if (!dateString) return ''
    
    // Handle different date formats
    let date
    if (dateString.includes('T')) {
      // ISO format
      date = new Date(dateString)
    } else if (dateString.includes('.')) {
      // German format (DD.MM.YYYY)
      const [day, month, year] = dateString.split('.')
      date = new Date(year, month - 1, day)
    } else {
      // Try parsing as regular date
      date = new Date(dateString)
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString // Return original string if parsing fails
    }

    // Format to DD.MM.YYYY
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (e) {
    console.error('Error formatting date:', e)
    return dateString
  }
}

// Add this new method after openPreview
const downloadInvoice = async (invoice) => {
  try {
    const doc = new jsPDF()
    
    // Add logo with smaller dimensions
    const logoPath = './OC_print_logo_icon_color.png'
    doc.addImage(logoPath, 'PNG', 10, 10, 22, 25)

    // Add company names with consistent positioning
    doc.setFontSize(20)
    doc.text('OddsComposer', 35, 25)
    doc.text('OddsComposer OÜ', 140, 25)
    
    // Reset font size for content
    doc.setFontSize(12)
    
    // Add invoice number
    doc.text(`Invoice #${invoice.id || 'INV-002'}`, 10, 40)
    
    // Calculate positions for right-aligned text
    const pageWidth = doc.internal.pageSize.width
    const rightMargin = 20
    const labelX = pageWidth - rightMargin - 60
    
    // Add dates with right alignment
    doc.text('Invoice Date:', labelX, 40)
    const invoiceDate = formatPDFDate(invoice.purchaseDate).split(' ')[0]
    doc.text(invoiceDate, pageWidth - rightMargin, 40, { align: 'right' })
    
    doc.text('Due Date:', labelX, 48)
    const dueDate = formatPDFDate(invoice.dueDate).split(' ')[0]
    doc.text(dueDate, pageWidth - rightMargin, 48, { align: 'right' })
    
    // Days left calculation
    // const currentDate = new Date()
    // const dueDateObj = new Date(invoice.dueDate)
    // const daysLeft = Math.ceil((dueDateObj - currentDate) / (1000 * 60 * 60 * 24))
    // doc.text(`${daysLeft} days left`, labelX, 56)
    
    // Add Bill To section
    doc.setFontSize(12)
    doc.text('Bill To:', 10, 55)
    doc.text(invoice.userName || 'Optibet SIA Gibraltar', 10, 62)
    doc.text('Address:', 10, 69)
    doc.text('Suite 6, Atlantic Suites, Europort Road,', 10, 76)
    doc.text('GX11 1AA, Gibraltar', 10, 83)
    
    // Right side information with adjusted horizontal alignment
    doc.setFontSize(12)
    
    // Format the invoice date correctly
    let invoiceDateFormatted = ''
    try {
      if (invoice.purchaseDate) {
        if (invoice.purchaseDate.includes('T')) {
          // Handle ISO format
          const date = new Date(invoice.purchaseDate)
          invoiceDateFormatted = date.toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })
        } else if (invoice.purchaseDate.includes('.')) {
          // Handle German format
          invoiceDateFormatted = invoice.purchaseDate.split(' ')[0]
        } else {
          // Try parsing as regular date
          const date = new Date(invoice.purchaseDate)
          if (!isNaN(date.getTime())) {
            invoiceDateFormatted = date.toLocaleDateString('de-DE', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            })
          }
        }
      }
      
      if (!invoiceDateFormatted) {
        invoiceDateFormatted = new Date().toLocaleDateString('de-DE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      }
    } catch (e) {
      console.error('Error formatting invoice date:', e)
      invoiceDateFormatted = new Date().toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }

    // Add items table
    const tableData = invoice.items.map(item => [
      item.description,
      item.quantity,
      formatPrice(item.price),
      formatPrice(item.quantity * item.price)
    ])
    
    doc.autoTable({
      startY: 95,
      head: [['Description', 'Qty', 'Price', 'Amount']],
      body: tableData,
      foot: [
        ['', '', 'Subtotal', formatPrice(invoice.amount)],
        ['', '', 'VAT (0%)', formatPrice(0)],
        ['', '', 'Total', formatPrice(invoice.amount)]
      ],
      theme: 'grid',
      headStyles: { fillColor: [51, 51, 51] },
      footStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0] }
    })
    
    // Add payment information
    const finalY = doc.lastAutoTable.finalY + 20
    
    doc.text('Bank Transfer Details:', 14, finalY)
    doc.text('Account Name: Odds Composer OÜ', 14, finalY + 7)
    doc.text('IBAN: EE601010220302953227', 14, finalY + 14)
    doc.text('SWIFT/BIC: EEUHEE2X', 14, finalY + 21)
    doc.text('Bank: SEB Pank', 14, finalY + 28)
    
    doc.text('Payment Reference:', 140, finalY)
    doc.text(`Description: Invoice #${invoice.id}`, 140, finalY + 7)
    doc.text('Payment due within 14 days', 140, finalY + 14)
    
    // Add VAT information
    doc.setFontSize(8)
    const vatY = finalY + 45
    doc.text('This transaction is subject to the reverse charge mechanism under Article 196 of Council Directive 2006/112/EC.', 14, vatY)
    doc.text('The liability to account for and pay VAT is shifted to the recipient of the services.', 14, vatY + 5)
    
    // Save the PDF
    doc.save(`invoice-${invoice.id}.pdf`)
  } catch (error) {
    console.error('Error generating PDF:', error)
  }
}

// Add these new refs and methods
const isEditing = ref(false)
const draggedItem = ref(null)
const draggedOverIndex = ref(null)

// Update openInvoiceModal to handle both create and edit with full data
const openInvoiceModal = (invoice = null) => {
  if (invoice) {
    // Edit mode
    isEditing.value = true
    
    try {
      // Parse the purchase date properly
      let purchaseDate
      if (invoice.purchaseDate) {
        // Convert German format to ISO format if needed
        if (invoice.purchaseDate.includes('.')) {
          const [datePart, timePart] = invoice.purchaseDate.split(' ')
          const [day, month, year] = datePart.split('.')
          const [hours, minutes] = timePart ? timePart.split(':') : ['00', '00']
          purchaseDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
        } else {
          // Try to parse as ISO format
          const date = new Date(invoice.purchaseDate)
          if (!isNaN(date.getTime())) {
            purchaseDate = date.toISOString().slice(0, 16)
          }
        }
      }

      // If no valid purchase date, don't set a default
      if (!purchaseDate) {
        purchaseDate = invoice.purchaseDate || new Date().toISOString().slice(0, 16)
      }

      // Parse or calculate the due date
      let dueDate
      if (invoice.dueDate) {
        if (invoice.dueDate.includes('.')) {
          const [day, month, year] = invoice.dueDate.split('.')
          dueDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        } else {
          const date = new Date(invoice.dueDate)
          if (!isNaN(date.getTime())) {
            dueDate = date.toISOString().slice(0, 10)
          }
        }
      }

      // If no valid due date, calculate from purchase date
      if (!dueDate) {
        const purchaseDateObj = new Date(purchaseDate)
        const dueDateObj = new Date(purchaseDateObj.getTime() + 14 * 24 * 60 * 60 * 1000)
        dueDate = dueDateObj.toISOString().slice(0, 10)
      }

      newInvoice.value = {
        ...invoice,
        purchaseDate,
        dueDate
      }

      // Set invoice items
      if (invoice.items && Array.isArray(invoice.items)) {
        invoiceItems.value = invoice.items.map(item => ({
          description: item.description || '',
          details: item.details || '',
          quantity: item.quantity || 1,
          price: item.price || 0
        }))
      } else {
        invoiceItems.value = [{
          description: invoice.fixtureName || '',
          details: invoice.league ? `${invoice.fixtureName || ''}\n${invoice.league}` : '',
          quantity: 1,
          price: invoice.amount || 0
        }]
      }
    } catch (e) {
      console.error('Error parsing dates:', e)
      const now = new Date()
      newInvoice.value = {
        ...invoice,
        purchaseDate: now.toISOString().slice(0, 16),
        dueDate: new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      }

      // Ensure invoice items are set even in error case
      if (!invoiceItems.value || !invoiceItems.value.length) {
        invoiceItems.value = [{
          description: invoice.fixtureName || '',
          details: invoice.league ? `${invoice.fixtureName || ''}\n${invoice.league}` : '',
          quantity: 1,
          price: invoice.amount || 0
        }]
      }
    }
  } else {
    // Create mode - set default dates
    const now = new Date()
    const defaultDueDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
    
    newInvoice.value = {
      ...newInvoice.value,
      purchaseDate: now.toISOString().slice(0, 16),
      dueDate: defaultDueDate.toISOString().slice(0, 10)
    }
    invoiceItems.value = []
  }
  isCreateModalOpen.value = true
}

// Update handleSubmit to format dates consistently
const handleSubmit = async () => {
  try {
    // Ensure we're using the selected dates
    const purchaseDate = newInvoice.value.purchaseDate
    const dueDate = newInvoice.value.dueDate
    
    const invoiceData = {
      ...newInvoice.value,
      // Keep the dates as they were selected
      purchaseDate: purchaseDate,
      dueDate: dueDate,
      items: invoiceItems.value,
      subtotal: subtotal.value,
      total: subtotal.value,
      vatRate: 0
    }

    if (isEditing.value) {
      await invoiceStore.updateInvoice(invoiceData)
    } else {
      await invoiceStore.createInvoice(invoiceData)
    }

    isCreateModalOpen.value = false
    // Reset form
    newInvoice.value = {
      userName: '',
      fixtureName: '',
      league: '',
      amount: '',
      paymentMethod: 'credit_card',
      cardLast4: '',
      status: 'paid',
      purchaseDate: '',  // Don't set default date
      dueDate: ''       // Don't set default date
    }
    invoiceItems.value = []
  } catch (error) {
    console.error('Error in form submission:', error)
  }
}

// Add this new method after createInvoice
const fillDemoData = () => {
  // Set demo invoice details
  newInvoice.value = {
    userName: 'John Smith',
    cardLast4: '4242',
    status: 'paid',
    purchaseDate: new Date().toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', ''),
    dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  // Set demo invoice items
  invoiceItems.value = [
    {
      description: 'Monthly Subscription',
      details: 'Access to 120 games',
      quantity: 1,
      price: 960.00
    },
    {
      description: 'Extra Matches',
      details: 'Los Angeles Lakers vs Boston Celtics\nNBA',
      quantity: 5,
      price: 12.00
    }
  ]
}

// Add new ref
const isServicesModalOpen = ref(false)

// Add new method
const addServiceToInvoice = (service) => {
  invoiceItems.value.push(service)
  isServicesModalOpen.value = false
}

// Add this new method after addInvoiceItem
const removeInvoiceItem = (index) => {
  invoiceItems.value.splice(index, 1)
}

// Add these new methods
const onDragStart = (index) => {
  draggedItem.value = index
}

const onDragOver = (e, index) => {
  e.preventDefault()
  draggedOverIndex.value = index
}

const onDrop = (e) => {
  e.preventDefault()
  if (draggedItem.value !== null && draggedOverIndex.value !== null) {
    // Get the items
    const items = [...invoiceItems.value]
    const draggedItemValue = items[draggedItem.value]
    
    // Remove dragged item
    items.splice(draggedItem.value, 1)
    
    // Add it at the new position
    items.splice(draggedOverIndex.value, 0, draggedItemValue)
    
    // Update the list
    invoiceItems.value = items
  }
  
  // Reset
  draggedItem.value = null
  draggedOverIndex.value = null
}

const onDragEnd = () => {
  draggedItem.value = null
  draggedOverIndex.value = null
}

// Add a new helper method for price formatting
const formatPrice = (price) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(price)
}

// Add computed property for unique clients
const uniqueClients = computed(() => {
  const clients = new Set(invoices.value.map(inv => inv.userName))
  return Array.from(clients).sort()
})
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold">Invoice Management</h1>
        <p class="text-gray-500">Dashboard / Invoices</p>
      </div>
      
      <div class="flex gap-3">
        <button class="btn-primary" @click="openInvoiceModal()">
          <i class="fas fa-plus mr-2"></i> Create Invoice
        </button>
        <button class="btn-secondary">
          <i class="fas fa-filter mr-2"></i> Filter
        </button>
        <button class="btn-secondary">
          <i class="fas fa-download mr-2"></i> Export
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-5 gap-6 mb-8">
      <StatCard
        title="Total Revenue"
        :value="stats.totalRevenue"
        :formatter="formatPrice"
        icon="dollar-sign"
      />
      <StatCard
        title="Monthly Revenue"
        :value="stats.monthlyRevenue"
        :formatter="formatPrice"
        icon="chart-line"
      />
      <StatCard
        title="Total Purchases"
        :value="stats.totalPurchases"
        icon="shopping-cart"
      />
      <StatCard
        title="Monthly Purchases"
        :value="stats.monthlyPurchases"
        icon="calendar"
      />
      <StatCard
        title="Average Ticket"
        :value="stats.averageTicketSize"
        :formatter="formatPrice"
        icon="ticket-alt"
      />
    </div>

    <!-- Search and Filters -->
    <div class="flex items-center gap-4 mb-6">
      <div class="flex gap-2">
        <!-- Client Filter -->
        <CustomDropdown
          v-model="filters.client"
          :options="uniqueClients"
          placeholder="All Clients"
          class="w-[180px]"
        />
        
        <!-- Status Filter -->
        <CustomDropdown
          v-model="filters.status"
          :options="['paid', 'pending', 'failed']"
          placeholder="All Status"
          class="w-[160px]"
        />
        
        <!-- Search -->
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search invoices..."
            class="form-input pl-10 w-[280px] bg-white"
          >
          <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
        </div>
      </div>
    </div>

    <!-- Invoices Table -->
    <div class="bg-white rounded-lg shadow">
      <table class="w-full">
        <thead class="bg-gray-700 text-white">
          <tr>
            <th class="py-3 px-4 text-left">INVOICE ID</th>
            <th class="py-3 px-4 text-left">CLIENT</th>
            <th class="py-3 px-4 text-left">GAME</th>
            <th class="py-3 px-4 text-left">AMOUNT</th>
            <th class="py-3 px-4 text-left">STATUS</th>
            <th class="py-3 px-4 text-left">PAYMENT METHOD</th>
            <th class="py-3 px-4 text-left">DATE</th>
            <th class="py-3 px-4 text-right">ACTIONS</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr 
            v-for="invoice in filteredInvoices" 
            :key="invoice.id" 
            class="hover:bg-gray-50 cursor-pointer"
            @click="openPreview(invoice)"
          >
            <td class="py-3 px-4 text-pink-500">{{ invoice.id }}</td>
            <td class="py-3 px-4">{{ invoice.userName }}</td>
            <td class="py-3 px-4">
              <div>{{ invoice.fixtureName }}</div>
              <div class="text-sm text-gray-500">{{ invoice.league }}</div>
            </td>
            <td class="py-3 px-4 font-medium">{{ formatPrice(invoice.amount) }}</td>
            <td class="py-3 px-4">
              <span 
                class="px-2 py-1 rounded-full text-sm"
                :class="{
                  'bg-green-100 text-green-700': invoice.status === 'paid',
                  'bg-yellow-100 text-yellow-700': invoice.status === 'pending',
                  'bg-red-100 text-red-700': invoice.status === 'failed'
                }"
              >
                {{ invoice.status }}
              </span>
            </td>
            <td class="py-3 px-4">
              <div class="flex items-center gap-2">
                <i class="fas fa-credit-card text-gray-400"></i>
                <span>•••• {{ invoice.cardLast4 }}</span>
              </div>
            </td>
            <td class="py-3 px-4">{{ invoice.purchaseDate }}</td>
            <td class="py-3 px-4">
              <div class="flex justify-end gap-2">
                <button 
                  class="text-gray-400 hover:text-gray-600" 
                  @click.stop="openInvoiceModal(invoice)"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button 
                  class="text-gray-400 hover:text-gray-600" 
                  @click="openPreview(invoice)"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button 
                  class="text-gray-400 hover:text-gray-600" 
                  @click="downloadInvoice(invoice)"
                >
                  <i class="fas fa-download"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Invoice Preview Modal -->
    <InvoicePreview
      v-if="isPreviewOpen"
      :invoice="selectedInvoice"
      @close="isPreviewOpen = false"
      @download="downloadInvoice"
    />

    <!-- Add Create Invoice Modal -->
    <div v-if="isCreateModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 overflow-hidden">
      <div class="bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh] paper-width scale-paper">
        <!-- Modal Header (fixed) -->
        <div class="bg-gray-50 px-8 py-4 rounded-t-lg border-b flex justify-between items-center flex-shrink-0">
          <div class="flex items-center gap-4">
            <img src="/OC_print_logo_icon_color.svg" alt="Logo" class="h-8 w-8">
            <div>
              <h2 class="text-xl font-bold text-gray-800">
                {{ isEditing ? 'Edit Invoice' : 'Create New Invoice' }}
              </h2>
              <p class="text-sm text-gray-500">
                Invoice #{{ isEditing ? newInvoice.id : 'DRAFT' }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-4">
        
            <button @click="isCreateModalOpen = false" class="text-gray-500 hover:text-gray-700">
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        <!-- Form Container (scrollable) -->
        <form @submit.prevent="handleSubmit" class="flex flex-col flex-1 overflow-hidden">
          <!-- Scrollable Content -->
          <div class="flex-1 overflow-y-auto">
            <div class="paper-min-height bg-white p-8">
              <!-- Top Section -->
              <div class="grid grid-cols-2 gap-x-16 mb-12">
                <!-- Left Side -->
                <div>
                  <h3 class="text-gray-700 font-medium mb-3">Bill To:</h3>
                  <div class="space-y-4">
                    <div>
                      <input 
                        v-model="newInvoice.userName"
                        type="text"
                        required
                        placeholder="Client Name"
                        class="form-input"
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <input 
                        v-model="newInvoice.cardLast4"
                        type="text"
                        required
                        
                        placeholder="Address"
                        class="form-input"
                      >
                    </div>
                    <!-- <div class="flex items-center gap-2">
                      <i class="fas fa-credit-card text-gray-400"></i>
                      <input 
                        v-model="newInvoice.cardLast4"
                        type="text"
                        required
                        maxlength="4"
                        pattern="\d{4}"
                        placeholder="Card ending in ####"
                        class="form-input"
                      >
                    </div> -->
                  </div>
                </div>

                <!-- Right Side -->
                <div class="space-y-4">
                  <div class="flex justify-between">
                    <span class="text-gray-600">Invoice Date:</span>
                    <input 
                      v-model="newInvoice.purchaseDate"
                      type="datetime-local"
                      required
                      class="form-input w-48"
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">Due Date:</span>
                    <input 
                      v-model="newInvoice.dueDate"
                      type="date"
                      required
                      class="form-input w-48"
                    >
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">Status:</span>
                    <select 
                      v-model="newInvoice.status"
                      class="form-input w-48"
                    >
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Invoice Items Table -->
              <div class="bg-white rounded-lg border mb-12">
                <table class="w-full">
                  <thead class="bg-gray-50 border-b">
                    <tr>
                      <th class="py-3 px-4 text-left text-gray-600">Description</th>
                      <th class="py-3 px-4 text-center text-gray-600 w-24">Qty</th>
                      <th class="py-3 px-4 text-right text-gray-600 w-32">Price</th>
                      <th class="py-3 px-4 text-right text-gray-600 w-32">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="invoiceItems.length === 0">
                      <td colspan="4" class="py-8 text-center text-gray-500">
                        No items added yet. Click "Add Item" or "Manage Services" to add items to this invoice.
                      </td>
                    </tr>
                    <tr 
                      v-for="(item, index) in invoiceItems" 
                      :key="index" 
                      class="border-b group"
                      draggable="true"
                      @dragstart="onDragStart(index)"
                      @dragover="onDragOver($event, index)"
                      @drop="onDrop"
                      @dragend="onDragEnd"
                      :class="{
                        'opacity-50': draggedItem === index,
                        'border-t-2 border-pink-500': draggedOverIndex === index && draggedItem !== index
                      }"
                    >
                      <td class="py-3 px-4">
                        <div class="flex items-center  gap-2">
                          <button 
                            type="button"
                            class="cursor-move text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <i class="fas fa-grip-vertical"></i>
                          </button>
                          <div class="flex-1">
                            <input 
                              v-model="item.description"
                              type="text"
                              class="form-input"
                              placeholder="Enter item description"
                            >
                         
                          </div>
                          <button 
                            type="button"
                            @click="removeInvoiceItem(index)"
                            class="text-red-400 hover:text-red-600 mt-2"
                            title="Remove Item"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                      <td class="py-3 px-4">
                        <input 
                          v-model.number="item.quantity"
                          type="number"
                          min="1"
                          class="form-input text-center"
                        >
                      </td>
                      <td class="py-3 px-4">
                        <input 
                          v-model.number="item.price"
                          type="number"
                          min="0"
                          step="0.01"
                          class="form-input text-right"
                        >
                      </td>
                      <td class="py-3 px-4 text-right">
                        {{ formatPrice(item.quantity * item.price) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot class="bg-gray-50">
                    <tr>
                      <td colspan="2" class="py-3 px-4">
                        <div class="flex gap-3">
                          <button 
                            type="button"
                            class="text-pink-500 hover:text-pink-600"
                            @click="addInvoiceItem"
                          >
                            <i class="fas fa-plus mr-1"></i> Add Item
                          </button>
                          <button 
                            type="button"
                            @click="isServicesModalOpen = true"
                            class="text-pink-500 hover:text-pink-600"
                          >
                            <i class="fas fa-cog mr-1"></i> Manage Services
                          </button>
                        </div>
                      </td>
                      <td class="py-3 px-4 text-right font-medium">Subtotal</td>
                      <td class="py-3 px-4 text-right">
                        {{ formatPrice(subtotal) }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="2"></td>
                      <td class="py-3 px-4 text-right font-medium">VAT (0%)</td>
                      <td class="py-3 px-4 text-right">
                        {{ formatPrice(0) }}
                      </td>
                    </tr>
                    <tr class="font-bold">
                      <td colspan="2"></td>
                      <td class="py-3 px-4 text-right">Total</td>
                      <td class="py-3 px-4 text-right text-pink-500">
                        {{ formatPrice(subtotal) }}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <!-- Payment Information -->
              <div class="grid grid-cols-2 gap-x-16 mb-12">
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h3 class="font-medium text-gray-700 mb-3">Bank Transfer Details:</h3>
                  <div class="space-y-2 text-gray-600">
                    <p>Account Name: Odds Composer OÜ</p>
                    <p>Reg. No: 17035273</p>
                    <p>IBAN: EE601010220302953227</p>
                    <p>SWIFT/BIC: EEUHEE2X</p>
                    <p>Bank: SEB Pank</p>
                  </div>
                </div>
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h3 class="font-medium text-gray-700 mb-3">Payment Reference:</h3>
                  <p class="text-gray-600">Please include the invoice number in the payment reference</p>
                  <p class="text-gray-600 mt-2">Payment due within 14 days</p>
                </div>
              </div>

              <!-- VAT Information -->
              <div class="bg-gray-50 p-4 rounded-lg mb-12 text-gray-600 text-sm">
                <p>This transaction is subject to the reverse charge mechanism under Article 196 of Council Directive 2006/112/EC.</p>
                <p>The liability to account for and pay VAT is shifted to the recipient of the services.</p>
              </div>
            </div>
          </div>

          <!-- Footer (fixed) -->
          <div class="flex justify-between items-center gap-3 px-8 py-4 border-t bg-white flex-shrink-0">
            <div class="text-sm text-gray-500">
              Preview in A4 format
            </div>
            <div class="flex gap-3">
              <button 
                type="button" 
                class="btn-secondary"
                @click="isCreateModalOpen = false"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                class="btn-primary"
              >
                {{ isEditing ? 'Update Invoice' : 'Create Invoice' }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Services Manage Modal -->
    <ServicesManageModal 
      v-if="isServicesModalOpen"
      :show="isServicesModalOpen"
      @close="isServicesModalOpen = false"
      @select="addServiceToInvoice"
    />
  </div>
</template>

<style scoped>
.btn-secondary {
  @apply px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg 
         hover:bg-gray-50 transition-colors duration-200;
}

.btn-primary {
  @apply px-4 py-2 text-white bg-pink-500 border border-pink-500 rounded-lg 
         hover:bg-pink-600 transition-colors duration-200;
}

/* Add these new styles */
.cursor-pointer {
  cursor: pointer;
}

/* Prevent action buttons from triggering row click */
.flex.justify-end button {
  position: relative;
  z-index: 10;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
         focus:outline-none focus:ring-1 focus:ring-pink-500 focus:border-pink-500
         text-gray-700 bg-white;
}

/* Update modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Smooth scrolling for the form content */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: theme('colors.gray.300') theme('colors.gray.100');
}

.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: theme('colors.gray.100');
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: theme('colors.gray.300');
  border-radius: 4px;
  border: 2px solid theme('colors.gray.100');
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: theme('colors.gray.400');
}

/* A4 Paper styling - scale-paper avoids LightningCSS parse error with scale-[0.85] */
@media screen {
  .scale-paper {
    transform: scale(0.85);
    transform-origin: center;
  }
}

/* Print styles - paper-width/paper-min-height avoid LightningCSS parse error with bracket notation */
@media print {
  .paper-width {
    width: 210mm;
  }

  .paper-min-height {
    min-height: 297mm;
  }

  .scale-paper {
    transform: none;
  }
}

/* Adjust form inputs for A4 */
.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
         focus:outline-none focus:ring-1 focus:ring-pink-500 focus:border-pink-500
         text-gray-700 bg-white text-base;
}

/* Add some print-friendly styles */
@media print {
  .bg-gray-50 {
    background-color: white !important;
  }
  
  .border {
    border-color: #e5e7eb !important;
  }
}

/* Drag and drop styles */
.group {
  transition: all 0.2s ease;
}

.group.dragging {
  opacity: 0.5;
  background: theme('colors.gray.50');
}

.group.drag-over {
  border-top: 2px solid theme('colors.pink.500');
}
</style> 